import { useMemo } from "react";
import { useParticipants } from "@livekit/components-react";
import { t } from "@/i18n-js/instance";
import { useToast } from "@/react/components/shared/uikit/ToastV2";
import { notifyBugsnag } from "@/react/helpers/bugsnagHelpers";
import { useElectronAPI } from "@/react/hooks/utils/useElectronAPI";
import type { ProviderScreenSharing } from "../types";
import { ACTION_TYPES, sendSignal } from "./helpers";
import { useLivekitLocalParticipant } from "./useLivekitLocalParticipant";

const OS_ERROR = "SystemDeniedPermission";

export const useLivekitScreenSharing = (
  canOverride = false,
): ProviderScreenSharing => {
  const { error } = useToast();
  const localParticipant = useLivekitLocalParticipant();
  const participants = useParticipants();
  const { isElectron } = useElectronAPI();
  const sharingParticipant = useMemo(
    () => participants.find(participant => participant.isScreenShareEnabled),
    [participants],
  );
  const isSomeoneScreenSharing = !!sharingParticipant;
  const isScreenSharingEnabled = !!localParticipant?.isScreenShareEnabled;
  const toggleScreenSharing = async () => {
    if (!isScreenSharingEnabled && isSomeoneScreenSharing && !canOverride) {
      return;
    }
    try {
      const track = await localParticipant.setScreenShareEnabled(
        !isScreenSharingEnabled,
        {
          audio: true,
          systemAudio: "include",
        },
      );
      if (track && sharingParticipant && !sharingParticipant.isLocal) {
        // Override remote screenshare
        await sendSignal(
          ACTION_TYPES.STOP_SCREENSHARE,
          localParticipant,
          sharingParticipant.identity,
        );
      }
    } catch (err: unknown) {
      if (isElectron) {
        error(t("live_streams.room.electron_screen_sharing_browser_error"));
        return;
      }
      if (err instanceof Error && err.name === OS_ERROR) {
        error(t("live_streams.room.screen_sharing_os_error"));
        return;
      }
      error(t("live_streams.room.screen_sharing_browser_error"));

      console.error(err);
      if (err instanceof Error) {
        notifyBugsnag(err);
      }
    }
  };

  return {
    isScreenSharingEnabled,
    isSomeoneScreenSharing,
    toggleScreenSharing,
  };
};
